import React from 'react';

export type FmButtonVariant = 'default' | 'bordered' | 'primary' | 'text' | 'link';
export type FmButtonSize = 'sm' | 'md' | 'lg';

export interface FmButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: FmButtonVariant;
  size?: FmButtonSize;
  leadingIcon?: React.ReactNode;
  /**
   * Icon after the label, for a move that carries the owner onward (a guided
   * step's forward action). Decorative: the label names the action.
   */
  trailingIcon?: React.ReactNode;
  /** Show a spinner and disable the button (AntD Button `loading` equivalent). */
  loading?: boolean;
  /** Destructive styling (AntD Button `danger` equivalent). */
  danger?: boolean;
  /** Render as an anchor element instead of button. Use with `href`. */
  href?: string;
  /** Anchor target (e.g., '_blank'). Only used when `href` is set. */
  target?: string;
  /** Anchor rel attribute (e.g., 'noopener noreferrer'). Only used when `href` is set. */
  rel?: string;
}

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

const VARIANT_CLASS: Record<FmButtonVariant, string | null> = {
  default: null,
  bordered: 'btn-mini--bordered',
  primary: 'btn-mini--primary',
  text: 'btn-mini--text',
  link: 'btn-mini--link',
};

function hasTextContent(node: React.ReactNode): boolean {
  return React.Children.toArray(node).some((child) => {
    if (typeof child === 'string') return child.trim().length > 0;
    if (typeof child === 'number') return true;
    if (React.isValidElement<{ children?: React.ReactNode }>(child)) {
      return hasTextContent(child.props.children);
    }
    return false;
  });
}

function buttonClassName(args: {
  variant: FmButtonVariant;
  size: FmButtonSize;
  danger: boolean;
  loading: boolean;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
}): string {
  return cx(
    'btn-mini',
    VARIANT_CLASS[args.variant],
    args.size === 'md' && 'btn-mini--md',
    args.size === 'lg' && 'btn-mini--lg',
    args.danger && 'btn-mini--danger',
    args.loading && 'btn-mini--loading',
    args.className,
  );
}

function renderSpinnerAndIcon(
  leadingIcon: React.ReactNode,
  trailingIcon: React.ReactNode,
  loading: boolean,
  children: React.ReactNode,
  hideLeadingIcon: boolean,
): React.ReactNode {
  return (
    <>
      {loading ? (
        <span className="btn-mini__spinner" aria-hidden="true" />
      ) : leadingIcon ? (
        <span className="btn-mini__icon" aria-hidden={hideLeadingIcon || undefined}>
          {leadingIcon}
        </span>
      ) : null}
      {children}
      {trailingIcon && !loading ? (
        <span className="btn-mini__icon" aria-hidden="true">
          {trailingIcon}
        </span>
      ) : null}
    </>
  );
}

/** Small reusable action button aligned with the stand redesign styles.
 * Renders as <button> by default, or as <a> when `href` is provided.
 */
export const FmButton = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, FmButtonProps>(
  (
    {
      variant = 'default',
      size = 'sm',
      leadingIcon,
      trailingIcon,
      loading = false,
      danger = false,
      className,
      children,
      type = 'button',
      disabled,
      href,
      target,
      rel,
      ...rest
    },
    ref,
  ) => {
    const hideLeadingIcon = Boolean(
      hasTextContent(children) || rest['aria-label'] || rest['aria-labelledby'],
    );

    const classes = buttonClassName({ variant, size, danger, loading, className });
    const content = renderSpinnerAndIcon(
      leadingIcon,
      trailingIcon,
      loading,
      children,
      hideLeadingIcon,
    );

    if (href) {
      return (
        <a
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
          href={href}
          target={target}
          rel={rel}
          className={classes}
          aria-disabled={disabled || loading || undefined}
          aria-busy={loading || undefined}
          {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {content}
        </a>
      );
    }

    return (
      <button
        ref={ref as React.ForwardedRef<HTMLButtonElement>}
        type={type as React.ButtonHTMLAttributes<HTMLButtonElement>['type']}
        disabled={disabled || loading}
        className={classes}
        aria-busy={loading || undefined}
        {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {content}
      </button>
    );
  },
);

FmButton.displayName = 'FmButton';

export default FmButton;
